import axios from 'axios';
import Cookies from 'js-cookie';
import { startLoader, stopLoader } from '../other/GlobalLoader';

const getToken = Cookies.get('token');

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE}/`,
  headers: {
    // 'ngrok-skip-browser-warning': true,
    Authorization: `Bearer ${getToken && JSON.parse(getToken.split('j:')[1]).token}`,
  },
  // timeout: 15000,
});

instance.interceptors.request.use(
  function (config) {
    const method = String(config.method || 'get').toLowerCase();
    config.showGlobalLoader = !config.silent && method !== 'get';
    if (config.showGlobalLoader) startLoader();
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function (response) {
    if (response.config?.showGlobalLoader) stopLoader();
    return response;
  },
  function (error) {
    if (error?.config?.showGlobalLoader) stopLoader();
    if (error.response) {
      const status = error.response.status;
      if (status === 401) {
      }
      if (status === 403) {
        Cookies.remove('token')
        window.location.href = process.env.REACT_APP_STAGING_URL;
      }
    }
    return Promise.reject(error);
  }
);

const responseBody = (response) => response.data;

const requests = {
  get: (url, config) => instance.get(url, config).then(responseBody),
  post: (url, body, config) =>
    instance
      .post(url, body, config)
      .then(responseBody),

  put: (url, body, config) => instance.put(url, body, config).then(responseBody),
};

export const dashboardApi = {
  getContracts: (day) => {
    const params = new URLSearchParams();
    if (day) params.append('day', day);
    return requests.get(`api/dashboard/get?${params}`);
  },
  getGraphData: (day) => {
    const params = new URLSearchParams();
    if (day) params.append('day', day);
    return requests.get(`api/dashboard/graph/get?${params}`);
  },

  getGraphDataForExpired: (day) => {
    const params = new URLSearchParams();
    if (day) params.append('day', day);
    return requests.get(`/api/dashboard//graph/last-expiry/get?${params}`);
  },
};

export const clientsApi = {
  getAllClientData: (
    seId,
    page,
    sortField,
    sortOrder,
    filter,
    search,
    withoutContract,
    config
  ) => {
    const params = new URLSearchParams();

    if (seId) params.append('seId', seId);
    if (page) params.append('page', page);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filter) params.append('filterField', filter);
    if (search) params.append('search', search);
    if (withoutContract) params.append('withoutContract', withoutContract);

    return requests.get(`/api/client/get/all?${params.toString()}`, config);
  },
  getClientData: (
    clientId,
    page,
    status,
    sortField,
    sortOrder,
    filter,
    search
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (status) params.append('status', status);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filter) params.append('filterField', filter);
    if (search) params.append('search', search);

    return requests.get(`api/client/get/${clientId}?${params.toString()}`);
  },
  getClientOtherContracts: (
    clientId,
    page,
    status,
    sortField,
    sortOrder,
    filter,
    search
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (status) params.append('status', status);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filter) params.append('filterField', filter);
    if (search) params.append('search', search);

    return requests.get(
      `api/client/get/othercontract/${clientId}?${params.toString()}`
    );
  },
  getEmployeeName: (data) =>
    requests.get('/api/employee/get/all', { silent: true }),
  addClientData: (data) => requests.post('/api/client/create', data),
  updateClientData: (clientId, data) =>
    requests.put(`/api/client/update/${clientId}`, data),
};

export const contracts = {
  clientNameList: (typeId, search) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    return requests.get(`/api/client/get/list/${typeId}?${params.toString()}`, { silent: true })
  },
  contractsAllData: (
    page,
    file,
    status,
    sortField,
    sortOrder,
    filterField,
    search,
    expiredDays
  ) => {
    const params = new URLSearchParams();

    if (page) params.append('page', page);
    if (file) params.append('name', file);
    if (status) params.append('status', status);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filterField) params.append('filterField', filterField);
    if (search) params.append('search', search);
    if (expiredDays) params.append('expiredDays', expiredDays);

    return requests.get(`/api/contract/get/all?${params.toString()}`);
  },
  getContractsData: (clientId) => requests.get(`/api/contract/get/${clientId}`),
  getActivityData: (contractId) =>
    requests.get(`/api/contract/get/activity/${contractId}`),
  getProjectName: (data) =>
    requests.get('/api/project/get/list', { silent: true }),
  getBusinessUnitData: (data) =>
    requests.get('/api/businessunit/get/all', { silent: true }),
  addContractsData: (data) => requests.post('/api/contract/create', data),
  updateContractsData: (clientId, data) =>
    requests.put(`/api/contract/update/${clientId}`, data),
  pendingContracts: (page, sortField, sortOrder, filterField, search) => {
    const params = new URLSearchParams();

    if (page) params.append('page', page);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filterField) params.append('filterField', filterField);
    if (search) params.append('search', search);

    return requests.get(`/api/contract/get/pending?${params.toString()}`);
  },
  // background count for the sidebar badge, so it never covers the screen
  pendingContractsCount: () =>
    requests.get('/api/contract/get/pending?page=1', { silent: true }),
  FileDataAPI: (data) => requests.post('/api/contract/fileupload', data),
  getProjectDropDown: (clientId) =>
    requests.get(`/api/project/get/list/${clientId}`, { silent: true }),
  getSowDropDown: (projectId) =>
    requests.get(`/api/contract/get/sow/list/${projectId}`, { silent: true }),
  getpocDropDown: (pocId) =>
    requests.get(`/api/contract/get/poc/list/${pocId}`, { silent: true }),
};

export const projectApi = {
  getAllProjectData: (page, sortField, sortOrder, filterField, search, config) => {
    const params = new URLSearchParams();

    if (page) params.append('page', page);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filterField) params.append('filterField', filterField);
    if (search) params.append('search', search);

    return requests.get(`/api/project/get/all?${params.toString()}`, config);
  },
  getProjectData: (
    clientId,
    page,
    status,
    sortField,
    sortOrder,
    filterField,
    search
  ) => {
    const params = new URLSearchParams();

    if (page) params.append('page', page);
    if (status) params.append('status', status);
    if (sortField) params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (filterField) params.append('filterField', filterField);
    if (search) params.append('search', search);
    console.log("params", params)

    return requests.get(`/api/project/get/${clientId}/?${params.toString()}`);
  },
  getEmployeeName: (data) =>
    requests.get('/api/employee/get', { silent: true }),
  addProjectData: (data) => requests.post('/api/project/create', data),
  updateProjectData: (clientId, data) =>
    requests.put(`/api/project/update/${clientId}`, data),
};

export const artifactApi = {
  getTypes: () => requests.get('/api/artifact/types/get/all'),
  getProjectArtifacts: (projectId, empCode) => {
    const params = new URLSearchParams();
    if (empCode) params.append('empCode', empCode);
    return requests.get(
      `/api/artifact/project/get/${projectId}?${params.toString()}`
    );
  },
  getArtifact: (artifactId) => requests.get(`/api/artifact/get/${artifactId}`),
  updateArtifact: (artifactId, data) =>
    requests.put(`/api/artifact/update/${artifactId}`, data),
  updateStatus: (artifactId, data) =>
    requests.put(`/api/artifact/status/${artifactId}`, data),
  uploadVersion: (artifactId, formData) =>
    requests.post(`/api/artifact/upload/${artifactId}`, formData),
  addComment: (artifactId, data) =>
    requests.post(`/api/artifact/comment/${artifactId}`, data),
  // files are streamed through the API (not public S3 links), so fetch as a blob
  downloadVersion: (versionId) =>
    instance
      .get(`/api/artifact/version/download/${versionId}`, {
        responseType: 'blob',
      })
      .then((response) => response.data),
};

export const resourceApi = {
  getRoles: () => requests.get('/api/resource/roles/get/all'),
  createRole: (data) => requests.post('/api/resource/roles/create', data),
  getPlan: (projectId) => requests.get(`/api/resource/plan/get/${projectId}`),
  savePlan: (projectId, data) =>
    requests.put(`/api/resource/plan/save/${projectId}`, data),
  getUtilisation: (from, weeks) => {
    const params = new URLSearchParams({ from, weeks });
    return requests.get(`/api/resource/utilisation/get?${params.toString()}`);
  },
};

export const commentsApi = {
  getAllComments: (contractId) =>
    requests.get(`/api/comment/get/${contractId}`),
  addComments: (data) => requests.post(`/api/comment/create`, data),
};
